// Per-story biased-HCLG orchestration for the Vosk backend.
//
// amira-lm-build publishes one binary OpenFST `HCLG.fst` per story phrase to the CDN at
// `<base>/<storyId>_<phraseIndex>.fst`. ReaderManager primes them all at story selection
// (`primeStoryGraphs`, with progress for the download UI) into an in-memory per-story
// store — deliberately NOT persisted, since students rarely re-read a story and Cache
// Storage would only accumulate stale graphs. The backend pulls each phrase's bytes at
// decode time (`getStoryGraphBytes`) through one seam (`createBiasedGraphRecognizer`).
//
// LIVE end-to-end; there is no biased-decode flag.
// CursorOrchestrator.shouldDownloadStoryLmGraphs gates on local ASR plus the vosk backend
// and nothing else, so a defective corpus is immediately user-visible. The word-list
// fallback is not a clean safety valve on the zoov lineage either — that path has its own
// open OpenFst toolchain defect, so falling back trades biased decode for wrong words.
//
// Requires the vosk-v3 per-recognizer graph binding (lexa-asr-assets#5): biasing only
// takes effect when the binary HCLG IS the recognizer's decode graph, and
// `new KaldiRecognizer(sr, { graph })` builds over the caller's FST while reusing the
// already-parsed acoustic model. Stock 0.0.8 cannot inject an FST; the v2.x
// `Model.loadGraph` path is banned (it re-parsed the acoustic model per swap and stranded
// ~33 MB of WASM heap each time, lexa-asr-assets#33). Without the v3 binding VoskAsr
// throws BiasedGraphUnsupportedError before this seam and callers use the word-list
// grammar. Every failure here degrades to that grammar for one phrase, costs at most one
// decode window, and is marked so it never counts toward the session-failure teardown.

import { recordLocalAsrFailure, recordLocalAsrLifecycle } from '../localAsrLifecycle';

/**
 * CDN path (under ASSET_ROOT) where per-phrase HCLG graphs are published; matches the
 * date-stamped layout amira-lm-build pushes to
 * s3://amira-assets-cdn/vosk-browser-hclg/20260901-e3472855/ (the flat
 * `<storyId>_<phraseIndex>.fst` scheme lives beneath it).
 *
 * VERSIONED PER SYMBOL TABLE — which is NOT the same as per revision number. Each
 * corpus is numbered against one archive's own `graph/words.txt`, and a mismatched
 * FST loads WITHOUT error and decodes wrong words — no throw, no fallback to the
 * word-list grammar, no telemetry. The BARE `vosk-browser-hclg` corpus belongs to
 * the stock 152K-symbol lineage, the `-zoov-r1` corpus to a superseded vocabulary,
 * and the `-zoov-r2`/`-r3` corpora are defective builds (next paragraph); none may
 * be served here — and the defective/superseded set has since been DELETED from the
 * CDN, so a stale pin would 404 loudly rather than decode wrong words silently. NOTE
 * the current date-stamped scheme publishes the GOOD corpus beneath the same
 * `vosk-browser-hclg/` directory (`vosk-browser-hclg/<date>-<sha>`); voskBiasedGraph.test.js
 * asserts the pin matches that shape and carries the graph layer's words.txt sha.
 *
 * PAIRED WITH THE SPLIT-BUNDLE GRAPH LAYER (voskAssetManifest.js GRAPH_STEM):
 * both carry the same `graph/words.txt` (sha256 `e3472855…`, 36,038 symbols), which
 * this path's `-e3472855` suffix restates — so the per-phrase FSTs published here are
 * correctly numbered against the graph the model loads. (The graph companion's
 * `hclg_corpus.cdn_path` still names the older `-zoov-r4` corpus and cannot be re-cut
 * without a graph bump; the shared words.txt sha256 is the pairing that holds — see
 * below.)
 *
 * THE PATH SELF-DECLARES ITS PAIRING — `<publish-date>-<words.txt sha256 prefix>`.
 * This corpus (`20260901-e3472855`) is numbered against `graph/words.txt` sha256
 * `e3472855…`, the SAME symbol table the split-bundle graph layer (voskAssetManifest.js
 * GRAPH_STEM, `graph/1.0.0`) carries — so a corpus REFRESH (a new Browser PM LM Build:
 * more stories, same vocabulary) moves ONLY this constant, while the graph layer moves
 * only when `words.txt` itself changes. It supersedes the `-zoov-r4` corpus, which in
 * turn superseded the DEFECTIVE `-zoov-r2`/`-r3` corpora: their graph pipeline dropped
 * Kaldi's olabel_lookahead relabel permutation, so every FST bound each word to a
 * phonetic neighbor's acoustics (structurally valid, decodes wrong words — the
 * ASR-quality regression), and `-r3` additionally shipped word alternatives while its
 * manifest claimed none. Every corpus since is a full rebuild (amira-lm-build#46:
 * `--hcl-relabel` + `--no-alternatives`) against that same `graph/words.txt`, validated
 * to 0% WER on matched synthesized audio through the real runtime. Never serve the
 * r2/r3 paths again. Frozen predecessors still name older corpora — the monolith-era
 * `-zoov-r4` model manifest and the superseded `vosk-v3/` graph companion both predate
 * the defect's discovery and are immutable, and the graph layer's own `graph/1.0.0`
 * manifest still records `hclg_corpus.cdn_path: vosk-browser-hclg-zoov-r4` (it is
 * immutable and `words.txt` did not change, so it cannot be re-cut to name this
 * corpus); the shared `words_txt_sha256` (`e3472855…`), not the cdn_path string, is
 * the pairing that actually holds.
 *
 * A future revision that DOES change `graph/words.txt` requires a new corpus and
 * both constants (this path and GRAPH_STEM) moving in one change.
 *
 * The three ways that pairing can break, and where each is caught:
 *   - an unintended bump of either side — this constant and the graph-layer stem
 *     are pinned as INDEPENDENT literals in voskBiasedGraph.test.js, so the pin
 *     asserts each side on its own rather than deriving one from the
 *     other. Whichever side moves without thought fails its own assertion;
 *   - the archive published without its corpus, or vice versa — a 404 per phrase,
 *     which degrades loudly to the word-list grammar and is recorded as
 *     `graphs.storyUnpublished`.
 *   - a corpus built against a different `words.txt` than the graph layer — the
 *     r2/r3 defect is the live proof of the cost: every FST decoded a phonetic
 *     neighbor and nothing client-side could see it. The date-stamped scheme now
 *     embeds the corpus's `words.txt` sha256 prefix IN THE PATH, and
 *     voskBiasedGraph.test.js asserts that suffix equals the graph layer's
 *     `words_txt_sha256` — so a corpus flip to a mismatched symbol table fails in
 *     source/CI rather than silently decoding wrong words in the field.
 *
 * That last check is still only the PUBLISHER's claim, encoded in the name (same
 * trust level as the graph companion's `hclg_corpus.cdn_path`); nothing client-side
 * independently attests what bytes the corpus was actually built from. Fully closing
 * it needs a `words_txt_sha256` marker published at the corpus root so the two sides
 * can be compared against the real artifact — the asset-side ask is filed as
 * amira-lm-build#47. A runtime check against the graph companion manifest alone was
 * tried and reverted: it cannot see the corpus, so it only restated the model's
 * claim, while adding an unbounded fetch to the story-launch path and a futile retry
 * loop.
 */
export const VOSK_HCLG_ASSET_PATH = 'vosk-browser-hclg/20260901-e3472855';

/** Message surfaced when the biased-HCLG path is requested on a runtime that can't do it. */
export const BIASED_GRAPH_UNSUPPORTED_MESSAGE =
  'Biased HCLG decode requires the vosk-v3 split-bundle runtime (per-recognizer ' +
  'graph binding); this runtime cannot decode a per-phrase FST safely — stock ' +
  '0.0.8 cannot inject one, and the v2.x loadGraph path leaks a full acoustic ' +
  'model per swap (lexa-asr-assets#33). Falling back to the word-list grammar.';

/**
 * Thrown by VoskAsr's biased path when the loaded runtime lacks the v3
 * per-recognizer graph binding. Carries a stable `code` so callers can
 * distinguish the expected runtime limitation from a genuine failure and fall
 * back quietly. The code predates v3 (it named the v2 loadGraph capability) and
 * is deliberately unchanged so field telemetry stays comparable across the
 * migration.
 */
export class BiasedGraphUnsupportedError extends Error {
  /** @param {string} [message] */
  constructor(message = BIASED_GRAPH_UNSUPPORTED_MESSAGE) {
    super(message);
    this.name = 'BiasedGraphUnsupportedError';
    this.code = 'VOSK_GRAPH_LOAD_UNSUPPORTED';
  }
}

/**
 * Default CDN base for the per-phrase graphs, e.g.
 * `https://assets.app.amiralearning.com/vosk-browser-hclg/20260901-e3472855` — the
 * latest corpus, numbered against the split-bundle graph layer's words.txt; see
 * VOSK_HCLG_ASSET_PATH. Empty when ASSET_ROOT is not configured (tests,
 * storybook), in which case priming is skipped.
 * @returns {string}
 */
export function getDefaultHclgBaseUrl() {
  const root = typeof process !== 'undefined' && process.env && process.env.ASSET_ROOT;
  return root ? `https://${root}/${VOSK_HCLG_ASSET_PATH}` : '';
}

/**
 * Joins a CDN base URL and a relative path with exactly one slash. A fully-qualified
 * http(s) path is returned unchanged; everything else is joined to `base` so we never
 * emit an origin-relative ("/foo.fst") URL that would resolve against the app origin.
 * @param {string} base
 * @param {string} path
 * @returns {string}
 */
function joinUrl(base, path) {
  const p = String(path || '');
  if (/^https?:\/\//i.test(p)) {
    return p;
  }
  return `${String(base || '').replace(/\/+$/, '')}/${p.replace(/^\/+/, '')}`;
}

/**
 * URL of one phrase's biased `HCLG.fst`, following the flat publishing convention.
 * @param {string} baseUrl
 * @param {string} storyId
 * @param {number} phraseIndex
 * @returns {string}
 */
export function buildStoryGraphUrl(baseUrl, storyId, phraseIndex) {
  return joinUrl(baseUrl, `${storyId}_${phraseIndex}.fst`);
}

// OpenFst binary FSTs open with the FstHeader magic (0x7eb2fdd6, little-endian).
// Checked on every downloaded graph before it enters the store: a misrouted prefix
// that 200s an SPA index.html, or a truncated/empty object, is otherwise
// indistinguishable from a graph here and would go straight to the runtime's
// loadGraph. This is a FORMAT check, not an integrity check — it happily accepts
// a defective or wrong-symbol-table FST (that class needs the corpus-root
// attestation, amira-lm-build#47); it exists to catch not-a-graph-at-all bytes.
const OPENFST_MAGIC = [0xd6, 0xfd, 0xb2, 0x7e];
/** An FstHeader alone exceeds this; real phrase graphs are KBs. Smaller is junk. */
const MIN_GRAPH_BYTES = 32;

/**
 * The two failure classes are handled differently at the call site: wrong magic
 * is DETERMINISTIC (the URL serves something that is not a graph — a re-fetch
 * returns the same bytes), while a short body with intact magic is a truncated
 * transfer that a retry can genuinely fix.
 */
const GRAPH_BYTES = Object.freeze({
  Ok: 'ok',
  NotAGraph: 'notAGraph',
  Truncated: 'truncated',
});

/**
 * @param {ArrayBuffer|ArrayBufferView|null|undefined} buffer
 * @returns {string} One of GRAPH_BYTES.
 */
function classifyGraphBytes(buffer) {
  const size = buffer ? buffer.byteLength : 0;
  if (size >= OPENFST_MAGIC.length) {
    // Normalize views: new Uint8Array(view, 0, 4) would take the iterable
    // overload, ignore the range args, and copy the whole object.
    const head = ArrayBuffer.isView(buffer)
      ? new Uint8Array(buffer.buffer, buffer.byteOffset, OPENFST_MAGIC.length)
      : new Uint8Array(buffer, 0, OPENFST_MAGIC.length);
    if (!OPENFST_MAGIC.every((byte, i) => head[i] === byte)) {
      return GRAPH_BYTES.NotAGraph;
    }
  }
  return size >= MIN_GRAPH_BYTES ? GRAPH_BYTES.Ok : GRAPH_BYTES.Truncated;
}

/**
 * Phrase array for a story in EITHER shape it travels through the app: the raw
 * GraphQL/getStory shape nests phrases under `chapters[0].phrases`, while the
 * transformed props.story shape flattens exactly that array to `story.phrases`
 * (see API.js paginateStory). Both therefore index identically to the decode-time
 * `phraseIndex` and to the published `<storyId>_<n>.fst` files, so a caller can
 * read phrase N from whichever shape it was handed and mean the same phrase.
 *
 * The single place that knows about the two shapes. Callers reaching past this into
 * `story.phrases` have twice now silently worked on the props path and returned
 * nothing on the mainline `selectStory` path, which passes the raw shape.
 * @param {object|null} story
 * @returns {Array|null} Null when neither shape carries an array.
 */
export function getStoryPhrases(story) {
  const phrases = (story && story.phrases)
    || (story && story.chapters && story.chapters[0] && story.chapters[0].phrases);
  return Array.isArray(phrases) ? phrases : null;
}

/**
 * Phrase count for a story in either shape — see getStoryPhrases. 0 when neither
 * shape is present.
 * @param {object|null} story
 * @returns {number}
 */
export function getStoryPhraseCount(story) {
  const phrases = getStoryPhrases(story);
  return phrases ? phrases.length : 0;
}

/**
 * Whether a descriptor carries what the store lookup needs: a story id and an explicit
 * integer phrase index. Requiring the index (instead of defaulting to 0) means a
 * phrase whose index never reached the metadata simply decodes via the word-list
 * grammar rather than silently decoding against phrase 0's graph.
 * @param {object|null} descriptor
 * @returns {boolean}
 */
export function hasBiasedGraphDescriptor(descriptor) {
  return Boolean(
    descriptor
    && typeof descriptor === 'object'
    && descriptor.storyId
    && Number.isInteger(descriptor.phraseIndex)
    && descriptor.phraseIndex >= 0,
  );
}

/**
 * Scope segment interventions append to their biased cache key. The scope changes ONLY the
 * key — bytes still resolve through getStoryGraphBytes(storyId, phraseIndex) — so a phrase
 * and its interventions share one download and one parse while owning separate recognizers,
 * which is what lets an intervention stream against the phrase's HCLG without its
 * reset/flush touching the recognizer the phrase is mid-utterance on. Scoped per phrase,
 * not per target word.
 */
export const BIASED_GRAPH_SCOPE_INTERVENTION = 'iv';

/** Every scope biasedGraphCacheKey can append, for unscopedBiasedGraphKey to strip. */
const BIASED_GRAPH_SCOPES = Object.freeze([BIASED_GRAPH_SCOPE_INTERVENTION]);

/**
 * Stable cache key for a recognizer bound to one story phrase's biased graph.
 * An optional `scope` (see BIASED_GRAPH_SCOPE_INTERVENTION) keys an independent
 * recognizer over the same phrase's graph bytes.
 * @param {{storyId: string, phraseIndex: number, scope?: string}} descriptor
 * @returns {string}
 */
export function biasedGraphCacheKey(descriptor) {
  const d = descriptor || {};
  const base = `biased:${d.storyId || ''}#${Number.isInteger(d.phraseIndex) ? d.phraseIndex : ''}`;
  // Only a REGISTERED scope is appended, so this and unscopedBiasedGraphKey cannot
  // disagree. An unregistered scope silently produced a key nothing could strip, which
  // would double-count one graph in the SYSTEMIC_BIASED_FAILURE_KEYS tally.
  return BIASED_GRAPH_SCOPES.includes(d.scope) ? `${base}:${d.scope}` : base;
}

/**
 * Strips the scope from a biased cache key, yielding the key for the GRAPH the scoped
 * recognizers share. Inverse of biasedGraphCacheKey's suffix.
 *
 * Failure accounting counts graphs, not recognizers: a phrase and its intervention hold two
 * keys over one FST, so a threshold calibrated in graphs (SYSTEMIC_BIASED_FAILURE_KEYS)
 * would otherwise trip twice as fast. Matches registered scope suffixes rather than parsing
 * positionally, so a storyId containing '#' then ':' cannot merge graph identities.
 * @param {string} key
 * @returns {string}
 */
export function unscopedBiasedGraphKey(key) {
  const str = String(key || '');
  for (const scope of BIASED_GRAPH_SCOPES) {
    const suffix = `:${scope}`;
    if (str.endsWith(suffix)) {
      return str.slice(0, -suffix.length);
    }
  }
  return str;
}

// ============ In-memory per-story graph store ============
//
// Holds the active story's downloaded graphs for the session. One story at a time:
// priming a new story (or clearing) drops the previous one. Never persisted.

let storyGraphStore = { storyId: null, graphs: new Map() };
// Invalidates in-flight primes when a newer prime/clear supersedes them.
let primeToken = 0;

// Negative cache of PERMANENT per-phrase verdicts (403/404 not-published, or non-graph
// bytes), keyed `${storyId}#${phraseIndex}`. Without it, an unpublished-graph story — an
// EXPECTED state, and one that now routes every phrase to server (TT-1283) — would have
// ensureStoryGraph re-request the same 404 on every phrase prep (O(phraseCount) pointless
// requests). Story-scoped: cleared whenever the store is reset/re-primed for a new story.
const permanentGraphVerdicts = new Map();

/** Drops all stored graphs (story end / unmount / re-prime). */
export function clearStoryGraphs() {
  primeToken += 1;
  storyGraphStore = { storyId: null, graphs: new Map() };
  permanentGraphVerdicts.clear();
}

/**
 * Whether any graphs are stored for the story.
 * @param {string} storyId
 * @returns {boolean}
 */
export function hasStoryGraphs(storyId) {
  return Boolean(storyId) && storyGraphStore.storyId === storyId && storyGraphStore.graphs.size > 0;
}

/**
 * Whether the in-memory store is currently PRIMED for this story — true even when zero
 * graphs actually landed (all downloads gave up), unlike hasStoryGraphs. This is the
 * "is this still the active story?" signal ReaderManager's server-placement prep needs at
 * WRITE time (TT-1283): primeStoryGraphs sets storyGraphStore.storyId before the prep runs
 * (both inside the awaited primeAndWarmStory) and re-primes it to a new story on
 * supersession, so it is correct even while props.story has not yet caught up on the
 * story-launch path.
 * @param {string} storyId
 * @returns {boolean}
 */
export function isStoryGraphStorePrimedFor(storyId) {
  return Boolean(storyId) && storyGraphStore.storyId === storyId;
}

/**
 * The stored graph bytes for one phrase, as a Uint8Array VIEW over the stored buffer
 * (not a copy). LOAD-BEARING, not a convenience: the vosk-v3 runtime transfers a raw
 * ArrayBuffer to the worker ZERO-COPY — detaching it — but COPIES a typed-array view
 * (`.slice().buffer`) before transferring (model.ts, which cites this consumer). A
 * raw ArrayBuffer returned here would be detached by the first recognizer create, so
 * every re-read of the phrase — LRU eviction of its recognizer, a re-entered phrase —
 * would silently hand the worker zero bytes with no error. The view keeps the stored
 * buffer intact across any number of creates. Null when absent — either not
 * downloaded (yet) or a different story is primed.
 * @param {string} storyId
 * @param {number} phraseIndex
 * @returns {Uint8Array|null}
 */
export function getStoryGraphBytes(storyId, phraseIndex) {
  if (!storyId || storyGraphStore.storyId !== storyId) {
    return null;
  }
  const buffer = storyGraphStore.graphs.get(phraseIndex);
  return buffer ? new Uint8Array(buffer) : null;
}

/**
 * Whether one phrase's graph bytes are present in the store — a presence test that reads
 * clearer than `Boolean(getStoryGraphBytes(...))` at call sites that only need the yes/no
 * (getStoryGraphBytes allocates a fresh view; this does not). Sibling to hasStoryGraphs.
 * @param {string} storyId
 * @param {number} phraseIndex
 * @returns {boolean}
 */
export function hasStoryGraph(storyId, phraseIndex) {
  return Boolean(storyId) && storyGraphStore.storyId === storyId && storyGraphStore.graphs.has(phraseIndex);
}

// 404/403 mean the story's graphs are not published — retrying cannot help, and
// (until the LM pipeline publishes for a story) it is the EXPECTED state, so it
// must stay cheap and quiet: no retry, no per-file warn, and a story whose whole
// first batch comes back permanent short-circuits the remaining downloads.
const PERMANENT_STATUSES = [403, 404];

// In-flight per-file downloads, keyed by `${storyId}#${phraseIndex}`, SHARED by
// primeStoryGraphs and ensureStoryGraph. The between-phrase re-download (TT-1283)
// fires exactly when the up-front prime may still be pulling the same file on a
// weak network; without this map that would double-fetch the one URL and pile more
// contention onto the network that is already the problem. The value is the verdict
// promise; it self-clears on settle.
const inFlightGraphFetches = new Map();

/**
 * One fetch attempt, bounded END-TO-END — connection, headers, AND body: the timer
 * stays armed through arrayBuffer(), so a stalled response body cannot park a caller
 * any more than a black-holed request can. Aborts via AbortController when available
 * and settles the awaited promise regardless. Resolves `{ok, status}` for non-ok
 * responses (the caller classifies 404/403 as permanent) or `{ok: true, buffer}`.
 * @param {string} url
 * @param {Function} doFetch
 * @param {number} perFileTimeoutMs
 * @returns {Promise<{ok: boolean, status?: number, buffer?: ArrayBuffer}>}
 */
function fetchGraphAttempt(url, doFetch, perFileTimeoutMs) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      if (controller) {
        controller.abort();
      }
      reject(new Error(`voskBiasedGraph: graph fetch timed out after ${perFileTimeoutMs}ms ${url}`));
    }, perFileTimeoutMs);
    const settle = (fn) => (value) => { clearTimeout(timer); fn(value); };
    Promise.resolve(doFetch(url, controller ? { signal: controller.signal } : undefined))
      .then((response) => {
        if (!response || !response.ok) {
          settle(resolve)({ ok: false, status: response && response.status });
          return null;
        }
        return Promise.resolve(response.arrayBuffer())
          .then((buffer) => settle(resolve)({ ok: true, buffer }));
      })
      .catch(settle(reject));
  });
}

/**
 * Downloads ONE phrase graph's bytes with retry + format classification, returning a
 * verdict WITHOUT touching the store, counters, or telemetry — every caller owns those
 * (prime tracks its aggregate counters; ensure records its own reprime markers). Deduped
 * through `inFlightGraphFetches`: a concurrent caller for the same (storyId, phraseIndex)
 * joins the existing promise instead of issuing a second fetch.
 *
 * Verdict is one of:
 *   { kind: 'loaded', buffer }
 *   { kind: 'permanent', notAGraph: boolean, status?: number, bytes?: number }
 *   { kind: 'gaveUp', attempts: number, error: string }
 *
 * FIRST-CALLER-WINS: the dedupe key is `${storyId}#${phraseIndex}` only, so a caller that
 * joins an in-flight fetch inherits the first caller's `baseUrl`, `perFileTimeoutMs`, and
 * `retriesPerFile`. Intentional — prime and ensure target the same CDN base, and ensure's
 * wait is separately bounded by its `budgetMs` — but a future caller needing different
 * fetch parameters for the same file must fold them into the key.
 * @returns {Promise<object>}
 */
function acquireGraphBytes({ storyId, phraseIndex, baseUrl, doFetch, perFileTimeoutMs, retriesPerFile }) {
  const key = `${storyId}#${phraseIndex}`;
  // A permanent verdict (404/403 unpublished, or non-graph bytes) is deterministic — a
  // re-fetch returns the same thing — so serve it from the negative cache without hitting
  // the network again (keeps repeated ensureStoryGraph calls on an unpublished story cheap).
  const cachedPermanent = permanentGraphVerdicts.get(key);
  if (cachedPermanent) {
    return Promise.resolve(cachedPermanent);
  }
  const existing = inFlightGraphFetches.get(key);
  if (existing) {
    return existing;
  }
  const url = buildStoryGraphUrl(baseUrl, storyId, phraseIndex);
  const run = (async () => {
    for (let attempt = 0; attempt <= retriesPerFile; attempt += 1) {
      try {
        const result = await fetchGraphAttempt(url, doFetch, perFileTimeoutMs);
        if (!result.ok) {
          if (PERMANENT_STATUSES.includes(result.status)) {
            return { kind: 'permanent', notAGraph: false, status: result.status };
          }
          throw new Error(`voskBiasedGraph: graph fetch failed ${url} (${result.status})`);
        }
        const verdict = classifyGraphBytes(result.buffer);
        if (verdict === GRAPH_BYTES.NotAGraph) {
          // Wrong magic is deterministic — the URL serves something that is not a
          // graph (SPA index.html on a misrouted prefix), and a re-fetch returns the
          // same bytes. No retry; reported by the caller under its own key.
          return { kind: 'permanent', notAGraph: true, bytes: result.buffer ? result.buffer.byteLength : 0 };
        }
        if (verdict === GRAPH_BYTES.Truncated) {
          // Intact magic (or nothing at all) with a short body: a truncated transfer,
          // which a re-fetch can genuinely fix — retry like any transient.
          throw new Error(
            `voskBiasedGraph: graph bytes truncated ${url} `
            + `(${result.buffer ? result.buffer.byteLength : 0} bytes)`,
          );
        }
        return { kind: 'loaded', buffer: result.buffer };
      } catch (err) {
        if (attempt === retriesPerFile) {
          return { kind: 'gaveUp', attempts: retriesPerFile + 1, error: err?.message || String(err) };
        }
      }
    }
    // Unreachable: the final attempt always returns above.
    return { kind: 'gaveUp', attempts: retriesPerFile + 1, error: 'voskBiasedGraph: exhausted attempts' };
  })();
  inFlightGraphFetches.set(key, run);
  run.then(
    (verdict) => {
      if (inFlightGraphFetches.get(key) === run) {
        inFlightGraphFetches.delete(key);
      }
      // Cache permanent verdicts so a later prep for the same phrase does not re-fetch.
      if (verdict && verdict.kind === 'permanent') {
        permanentGraphVerdicts.set(key, verdict);
      }
    },
    () => {
      if (inFlightGraphFetches.get(key) === run) {
        inFlightGraphFetches.delete(key);
      }
    },
  );
  return run;
}

/**
 * Downloads every phrase graph for a story into the in-memory store, up front, with
 * count-based aggregate progress for the download UI. Resolves with a summary and
 * never rejects for per-file failures: a phrase whose download failed is simply
 * absent from the store and decodes via the word-list grammar. A 200 whose bytes
 * fail the OpenFst format check counts as a failed download — wrong magic is
 * permanent (`graphs.notAGraph`, feeds the story short-circuit), a truncated
 * body is retried then gives up loudly. A prime superseded by a newer
 * prime/clear stops contributing and reports `stale: true`.
 *
 * Two bounds keep this off the story-launch critical path on bad networks:
 * - `perFileTimeoutMs` aborts a black-holed request (stalled proxy, captive portal)
 *   so a hung fetch can never park a worker forever.
 * - `softDeadlineMs` (optional) resolves the returned promise early with
 *   `timedOut: true` while the remaining downloads CONTINUE in the background and
 *   keep landing in the store — the decode path retries store misses per window, so
 *   late graphs upgrade mid-story. Progress callbacks stop at the deadline (the
 *   caller has dismissed its UI by then).
 * @param {object} params
 * @param {string} params.storyId
 * @param {number} params.phraseCount One graph per phrase: indices 0..phraseCount-1.
 * @param {string} [params.baseUrl] CDN base; defaults to ASSET_ROOT's HCLG path.
 * @param {(fraction: number, detail: {completed: number, total: number}) => void} [params.onProgress]
 * @param {Function} [params.fetchImpl] Injectable fetch (tests).
 * @param {number} [params.concurrency] Parallel downloads (small files; 4 is plenty).
 * @param {number} [params.retriesPerFile] Extra attempts per file for transient blips.
 * @param {number} [params.perFileTimeoutMs] Per-attempt fetch timeout.
 * @param {number|null} [params.softDeadlineMs] Early-resolve deadline; null awaits all.
 * @returns {Promise<{ok: boolean, stale?: boolean, timedOut?: boolean, unavailable?: boolean, total: number, loaded: number, failed: number}>}
 */
export async function primeStoryGraphs({
  storyId,
  phraseCount,
  baseUrl = getDefaultHclgBaseUrl(),
  onProgress,
  fetchImpl,
  concurrency = 4,
  retriesPerFile = 1,
  perFileTimeoutMs = 15000,
  softDeadlineMs = null,
} = {}) {
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  const total = Number.isInteger(phraseCount) && phraseCount > 0 ? phraseCount : 0;
  if (!storyId || !baseUrl || total === 0 || typeof doFetch !== 'function') {
    // Unusable inputs prime nothing — and the previous story's graphs must not
    // outlive their story, so drop them here too.
    clearStoryGraphs();
    return { ok: false, total, loaded: 0, failed: total };
  }

  primeToken += 1;
  const token = primeToken;
  storyGraphStore = { storyId, graphs: new Map() };
  // A fresh prime (new story, or re-prime of this one) invalidates any cached permanent
  // verdicts — the phrases may since have been published.
  permanentGraphVerdicts.clear();

  let completed = 0;
  let loaded = 0;
  let deadlineHit = false;
  // Progress stops at the soft deadline (the caller dismissed its UI) AND on
  // supersession (a newer prime/clear owns the progress UI now) — otherwise an
  // in-flight file finishing after either event would drive a stale callback.
  const reportProgress = () => {
    if (!deadlineHit && token === primeToken && typeof onProgress === 'function') {
      onProgress(completed / total, { completed, total });
    }
  };

  let permanentFailures = 0;
  let notAGraphCount = 0;
  let storyUnavailable = false;

  // Fetch + classify + retry now live in the shared acquireGraphBytes helper (deduped
  // against ensureStoryGraph, TT-1283); this only applies the verdict to the prime's
  // store/counters/telemetry under the prime-token guard.
  const downloadOne = async (phraseIndex) => {
    const verdict = await acquireGraphBytes({
      storyId, phraseIndex, baseUrl, doFetch, perFileTimeoutMs, retriesPerFile,
    });
    if (verdict.kind === 'loaded') {
      if (token === primeToken) {
        storyGraphStore.graphs.set(phraseIndex, verdict.buffer);
        loaded += 1;
      }
      return;
    }
    if (verdict.kind === 'permanent') {
      permanentFailures += 1;
      if (verdict.notAGraph) {
        // Wrong magic is a misroute (SPA index.html on a bad prefix), recorded under
        // its OWN key so a corpus-wide false positive is visible in aggregate rather
        // than buried in downloadGaveUp strings.
        notAGraphCount += 1;
        recordLocalAsrFailure('graphs.notAGraph', {
          storyId,
          phraseIndex,
          bytes: verdict.bytes,
        });
      }
      return;
    }
    // gaveUp: a transient failure outlasted the retries. The up-front prime never
    // retries this phrase again, but the between-phrase ensureStoryGraph (TT-1283) can
    // re-prime it when the next phrase is prepared, so this is no longer permanent for
    // the whole read.
    recordLocalAsrFailure('graphs.downloadGaveUp', {
      storyId,
      phraseIndex,
      attempts: verdict.attempts,
      error: verdict.error,
    });
  };

  // Bounded-concurrency worker pool over the phrase indices, reporting progress as
  // each file settles (loaded or given up) so the bar always reaches 100%.
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < total && token === primeToken && !storyUnavailable) {
      const phraseIndex = nextIndex;
      nextIndex += 1;
      await downloadOne(phraseIndex);
      if (storyUnavailable) {
        return; // another worker short-circuited while this file was in flight
      }
      completed += 1;
      if (loaded === 0 && permanentFailures >= Math.min(concurrency, total)) {
        // The whole first batch 404'd with nothing loaded: the story's graphs are
        // not published. Skip the rest instead of hammering the CDN with N misses.
        storyUnavailable = true;
        completed = total;
        // Expected until the LM pipeline publishes this story, so it is recorded as a
        // known-cause degradation rather than shouted about, and the recorder writes the
        // single console line for it.
        recordLocalAsrFailure(
          'graphs.storyUnpublished',
          { storyId, total, probed: permanentFailures, notAGraph: notAGraphCount },
          // A 404ing story is the expected pre-publish state; a prefix serving
          // non-graph bytes (notAGraph > 0) is not.
          { expected: notAGraphCount === 0 },
        );
      }
      reportProgress();
    }
  };
  reportProgress();

  const summarize = () => {
    if (token !== primeToken) {
      return { ok: false, stale: true, total, loaded, failed: total - loaded };
    }
    if (storyUnavailable) {
      // `notAGraph` rides the summary because `unavailable` alone conflates two very
      // different states: a story the LM pipeline has not published yet (expected,
      // 404s) and a prefix serving non-graph bytes (a misroute — never expected).
      // Callers key their `expected` flag off it; without it a misroute would be
      // loud on graphs.storyUnpublished and silently expected on the caller's own
      // graphs.downloadIncomplete.
      return {
        ok: false, unavailable: true, notAGraph: notAGraphCount, total, loaded, failed: total - loaded,
      };
    }
    return { ok: loaded === total, total, loaded, failed: total - loaded };
  };

  const allDone = Promise.all(Array.from({ length: Math.min(concurrency, total) }, worker));
  // Null/undefined means "await everything"; 0 is a valid instant deadline
  // ("background everything, launch now").
  if (softDeadlineMs == null) {
    await allDone;
    return summarize();
  }

  const DEADLINE = Symbol('deadline');
  let deadlineTimer;
  const raced = await Promise.race([
    allDone,
    new Promise((resolve) => { deadlineTimer = setTimeout(() => resolve(DEADLINE), softDeadlineMs); }),
  ]);
  // Cancel the deadline timer when the downloads won the race (the common case), so a
  // successful prime never leaves a pending timer + closure alive until softDeadlineMs.
  clearTimeout(deadlineTimer);
  if (raced !== DEADLINE) {
    return summarize();
  }
  // Deadline hit: resolve now so the caller can launch the story, but let the
  // workers keep filling the store — the decode path upgrades mid-story.
  deadlineHit = true;
  return { ...summarize(), ok: false, timedOut: true };
}

/**
 * Re-download ONE phrase's graph into the CURRENT story's store, on demand, after the
 * up-front prime has already settled (TT-1283). This is what makes a `downloadGaveUp`
 * phrase recoverable: the up-front prime never retries a gave-up file, but a weak
 * network usually recovers mid-read, so re-attempting the specific missing phrase
 * during the end-of-phrase gap lets it decode on its real biased graph instead of the
 * defective word-list grammar.
 *
 * Store-preserving by design:
 * - Does NOT increment `primeToken`, so it never disturbs an in-flight up-front prime
 *   (its background workers keep filling the same store).
 * - Writes only when it still owns the store on completion (`primeToken` unchanged and
 *   the same `storyId` is primed) — a superseding prime/clear makes it a no-op.
 * - Deduped through `acquireGraphBytes`: if the prime is still pulling this very file,
 *   this joins that fetch rather than issuing a second one.
 *
 * A no-op that returns `{ ok: true, landed: true }` when the bytes are already present.
 *
 * @param {object} params
 * @param {string} params.storyId
 * @param {number} params.phraseIndex Story-relative index (matches the `<storyId>_<n>.fst` file).
 * @param {string} [params.baseUrl] CDN base; defaults to ASSET_ROOT's HCLG path.
 * @param {Function} [params.fetchImpl] Injectable fetch (tests).
 * @param {number} [params.perFileTimeoutMs] Per-attempt fetch timeout.
 * @param {number} [params.retriesPerFile] Extra attempts for transient blips.
 * @returns {Promise<{ok: boolean, landed: boolean, stale?: boolean}>}
 */
export async function ensureStoryGraph({
  storyId,
  phraseIndex,
  baseUrl = getDefaultHclgBaseUrl(),
  fetchImpl,
  perFileTimeoutMs = 15000,
  retriesPerFile = 1,
} = {}) {
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (
    !storyId
    || !baseUrl
    || !Number.isInteger(phraseIndex)
    || phraseIndex < 0
    || typeof doFetch !== 'function'
    || storyGraphStore.storyId !== storyId
  ) {
    return { ok: false, landed: false };
  }
  // Already downloaded (by the prime, a prior ensure, or a background worker).
  if (storyGraphStore.graphs.has(phraseIndex)) {
    return { ok: true, landed: true };
  }

  // Capture the token so a prime/clear that supersedes us mid-fetch is detected before
  // we write — the write must never land in a store that has moved on to another story.
  const token = primeToken;
  // Whether this call JOINS an already-in-flight fetch (the prime pulling the same file)
  // rather than starting its own. If so, a subsequent `loaded` is really the prime's
  // download landing, not a distinct recovery — flagged so the phraseReprimed metric can
  // exclude it (recovery RATE would otherwise read high).
  const joinedInFlight = inFlightGraphFetches.has(`${storyId}#${phraseIndex}`);
  const verdict = await acquireGraphBytes({
    storyId, phraseIndex, baseUrl, doFetch, perFileTimeoutMs, retriesPerFile,
  });

  if (token !== primeToken || storyGraphStore.storyId !== storyId) {
    return { ok: false, landed: false, stale: true };
  }
  if (verdict.kind === 'loaded') {
    storyGraphStore.graphs.set(phraseIndex, verdict.buffer);
    // Recovery signal: a phrase that would have decoded on the word-list grammar now
    // has its real graph. Recovery RATE is this (minus joinedInFlight) over downloadGaveUp.
    recordLocalAsrLifecycle('graphs.phraseReprimed', { storyId, phraseIndex, joinedInFlight });
    return { ok: true, landed: true };
  }
  // Still unavailable: the caller routes the phrase to server ASR (never the defective
  // word-list grammar). The server-fallback marker is recorded at the routing site.
  return { ok: false, landed: false };
}

// ============ Runtime seam ============

/**
 * INTEGRATION SEAM. Builds a graph-backed recognizer in ONE step:
 * `new model.KaldiRecognizer(sr, { graph })` decodes against the caller's binary
 * HCLG while REUSING the model's already-parsed acoustic model — no Model
 * rebuild, no staged loadGraph handoff (both deleted in the vosk-v3 runtime).
 *
 * Graph-ownership contract (vosk-v3 runtime): the recognizer owns its graph for
 * life and frees it on remove() — ownership is per-recognizer, never
 * shared-mutable. So each biased recognizer is a self-contained pairing of one
 * graph and one decoder, and caching one per phrase key in the LRU is correct.
 *
 * Buffer-ownership contract: pass `fstBytes` as a typed-array VIEW (what
 * getStoryGraphBytes returns), never as the store's raw ArrayBuffer. The runtime
 * transfers a raw ArrayBuffer zero-copy (detaching it) but copies a view before
 * transferring, which is what lets the story-graph store serve the same phrase
 * bytes again after an eviction or re-read.
 *
 * Whether the loaded runtime HAS the binding is not detectable from the model
 * object (a v2.x Model exposes the same surface); VoskAsr gates this path on the
 * resolved manifest (split-bundle `graphUrl` present) and throws
 * BiasedGraphUnsupportedError before reaching here on runtimes without it.
 * @param {object} model vosk-v3 model.
 * @param {ArrayBuffer|Uint8Array} fstBytes Binary OpenFST HCLG.fst bytes.
 * @param {{sampleRate: number}} params
 * @returns {Promise<object>} A graph-backed KaldiRecognizer.
 */
// `async` with no await is deliberate, not an oversight: it keeps the seam's
// Promise contract stable (the v2 seam was genuinely async, every caller awaits,
// and a future runtime that adds a create ack changes nothing here) and it
// normalizes a synchronous constructor throw into a rejection, so callers handle
// one error shape.
export async function createBiasedGraphRecognizer(model, fstBytes, { sampleRate } = {}) {
  return new model.KaldiRecognizer(sampleRate, { graph: fstBytes });
}

export const __VOSK_BIASED_GRAPH_TESTS__ = {
  joinUrl,
  classifyGraphBytes,
  GRAPH_BYTES,
};
